import React, { useEffect, memo } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
  Spinner,
} from 'reactstrap';
import { useFormik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import {
  useCreateMutation,
  useUpdatePatchMutation,
  useDetailQuery,
} from 'http-client/hooks/useCrud';
import { API_ENDPOINTS } from 'http-client/httpConstants';
import { handleFormErrors } from 'utils/formErrorHandler';

interface ItemUnitFormValues {
  unitName: string;
  notes: string;
  isActive: boolean;
}

const validationSchema = Yup.object({
  unitName: Yup.string()
    .trim()
    .required('Unit name is required')
    .max(100, 'Unit name must be at most 100 characters'),
  notes: Yup.string()
    .trim()
    .required('Notes are required')
    .max(500, 'Notes must be at most 500 characters'),
  isActive: Yup.boolean(),
});

interface ItemUnitCreateOrUpdateProps {
  isOpen: boolean;
  toggle: () => void;
  unitId?: string;
  onSuccess?: () => void;
}

const ItemUnitCreateOrUpdate: React.FC<ItemUnitCreateOrUpdateProps> = ({
  isOpen,
  toggle,
  unitId,
  onSuccess,
}) => {
  const isEditMode = Boolean(unitId);

  const { data: fetchedUnit, isLoading: loadingUnit } = useDetailQuery<ItemUnitFormValues>(
    API_ENDPOINTS.SAAS.ITEM_UNITS.GET_BY_ID(unitId ?? ''),
    {
      id: unitId ?? '',
    },
  );

  const { mutate: createUnit, isPending: creating } = useCreateMutation(
    API_ENDPOINTS.SAAS.ITEM_UNITS.BASE,
  );

  const { mutate: updateUnit, isPending: updating } = useUpdatePatchMutation(
    `${API_ENDPOINTS.SAAS.ITEM_UNITS.BASE}/${unitId}`,
  );

  const initialValues: ItemUnitFormValues = {
    unitName: '',
    notes: '',
    isActive: true,
    ...fetchedUnit,
  };

  const formik = useFormik<ItemUnitFormValues>({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values, formikHelpers: FormikHelpers<ItemUnitFormValues>) => {
      const payload = {
        ...values,
        unitName: values.unitName.trim(),
        notes: values.notes?.trim() || undefined,
      };

      const handleSuccess = () => {
        formik.resetForm();
        toggle();
        onSuccess?.();
      };

      const handleError = (error: any) => {
        handleFormErrors<ItemUnitFormValues>(error, formikHelpers);
      };

      if (isEditMode) {
        updateUnit(
          { data: values, params: { id: unitId } },
          { onSuccess: handleSuccess, onError: handleError },
        );
      } else {
        createUnit(payload, { onSuccess: handleSuccess, onError: handleError });
      }
    },
  });

  const handleClose = () => {
    formik.resetForm();
    toggle();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="lg">
      <ModalHeader toggle={handleClose}>
        {isEditMode ? 'Update Item Unit' : 'Create Item Unit'}
      </ModalHeader>
      <form onSubmit={formik.handleSubmit} noValidate>
        <ModalBody>
          {isEditMode && loadingUnit ? (
            <div className="text-center py-4">
              <Spinner color="primary" />
            </div>
          ) : (
            <>
              <FormGroup>
                <Label for="unitName">Unit Name *</Label>
                <Input
                  id="unitName"
                  name="unitName"
                  type="text"
                  placeholder="Enter unit name (e.g., Strip, Tablet, Bottle)"
                  value={formik.values.unitName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched.unitName && Boolean(formik.errors.unitName)}
                />
                {formik.touched.unitName && formik.errors.unitName && (
                  <small className="text-danger">{formik.errors.unitName}</small>
                )}
              </FormGroup>

              <FormGroup>
                <Label for="notes">Notes *</Label>
                <Input
                  id="notes"
                  name="notes"
                  type="textarea"
                  placeholder="Enter additional notes"
                  value={formik.values.notes}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched.notes && Boolean(formik.errors.notes)}
                />
                {formik.touched.notes && formik.errors.notes && (
                  <small className="text-danger">{formik.errors.notes}</small>
                )}
              </FormGroup>

              <FormGroup check>
                <Label check>
                  <Input
                    name="isActive"
                    type="checkbox"
                    checked={formik.values.isActive}
                    onChange={formik.handleChange}
                  />
                  Active
                </Label>
              </FormGroup>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button color="primary" type="submit" disabled={creating || updating || loadingUnit}>
            {creating || updating ? (
              <>
                <Spinner size="sm" color="light" /> {isEditMode ? 'Updating...' : 'Creating...'}
              </>
            ) : isEditMode ? (
              'Update'
            ) : (
              'Create'
            )}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default memo(ItemUnitCreateOrUpdate);
